import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';

import { displayNewsHeadline, fetchPassedNews, selectors } from 'state';
import { writeV4NewsDragData } from './assessmentUi';
import { recordDragDiagnostic } from 'services/dragDiagnostics';

export type V4NewsItem = {
  id: number | string;
  headline: string;
  rephrased_headline?: string | null;
  body?: string;
  timestamp?: string;
  importance?: number;
  geography?: string | null;
  sectors?: string[] | null;
};

type NewsGroupingMode = 'all' | 'geography' | 'sector';

type Props = {
  dragEnabled?: boolean;
};

const ALL_NEWS = 'all';
type NewsScope = 'new' | 'all';
const NEWS_DELIVERY_ANIMATION_MS = 340;
const NEWS_DELIVERY_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

const normalizedGeography = (value?: string | null) =>
  value?.trim() || 'Unknown';

const normalizedSector = (value?: string | null) =>
  value?.trim() || 'Unassigned';

export const groupV4News = (news: V4NewsItem[], mode: NewsGroupingMode) => {
  if (mode === 'all') return {} as Record<string, V4NewsItem[]>;

  return news.reduce<Record<string, V4NewsItem[]>>((groups, item) => {
    const labels =
      mode === 'sector'
        ? (item.sectors || []).map(normalizedSector).filter(Boolean)
        : [normalizedGeography(item.geography)];
    const groupLabels = labels.length ? labels : ['Unassigned'];

    groupLabels.forEach(label => {
      if (!groups[label]) groups[label] = [];
      groups[label].push(item);
    });
    return groups;
  }, {});
};

const NewsFeed: React.FC<Props> = ({ dragEnabled = false }) => {
  const dispatch = useDispatch();
  const news = useSelector(selectors.marketsNewsList) as V4NewsItem[];
  const lastNews = useSelector(selectors.lastNews) as unknown as
    | Record<string, V4NewsItem>
    | undefined;
  const lastNewsSource = useSelector(selectors.lastNewsSource);
  const currentPeriodNewsIds = useSelector(selectors.currentPeriodNewsIds);
  const { id, elapsed_days, news_fetched, fetching_news, news_grouping_mode } =
    useSelector(selectors.activeSession);
  const groupingMode = (news_grouping_mode || 'all') as NewsGroupingMode;
  const [activeGroup, setActiveGroup] = useState(ALL_NEWS);
  const [scope, setScope] = useState<NewsScope>('all');
  const [search, setSearch] = useState('');
  const draggedStory = useRef<HTMLElement>();
  const dragMarkTimeout = useRef<ReturnType<typeof setTimeout>>();
  const cancelPendingDragMark = useCallback(() => {
    if (dragMarkTimeout.current === undefined) return;
    clearTimeout(dragMarkTimeout.current);
    dragMarkTimeout.current = undefined;
  }, []);
  const clearDrag = useCallback(() => {
    if (draggedStory.current) {
      recordDragDiagnostic('source.cleanup', {
        news: Number(draggedStory.current.dataset.newsId),
      }, true);
    }
    cancelPendingDragMark();
    draggedStory.current?.classList.remove('is-dragging');
    draggedStory.current = undefined;
  }, [cancelPendingDragMark]);
  const markDraggedAfterStart = useCallback(
    (element: HTMLElement) => {
      cancelPendingDragMark();
      draggedStory.current = element;
      recordDragDiagnostic('source.mark.scheduled', {
        news: Number(element.dataset.newsId),
      }, true);
      dragMarkTimeout.current = setTimeout(() => {
        dragMarkTimeout.current = undefined;
        if (draggedStory.current === element && element.isConnected) {
          element.classList.add('is-dragging');
          recordDragDiagnostic('source.mark.applied', {
            news: Number(element.dataset.newsId),
          }, true);
        }
      }, 0);
    },
    [cancelPendingDragMark],
  );
  useEffect(() => {
    clearDrag();
    const clearWhenHidden = () => {
      if (document.hidden) clearDrag();
    };
    // A cancelled/outside drop may never reach the source's React handler.
    // pointerdown also recovers stale feedback once normal input resumes.
    const events = ['dragstart', 'dragend', 'drop', 'pointerdown'] as const;
    events.forEach(name => document.addEventListener(name, clearDrag, true));
    document.addEventListener('visibilitychange', clearWhenHidden);
    return () => {
      clearDrag();
      events.forEach(name =>
        document.removeEventListener(name, clearDrag, true),
      );
      document.removeEventListener('visibilitychange', clearWhenHidden);
    };
  }, [clearDrag, dragEnabled, elapsed_days, id]);
  const newsItemElements = useRef(new Map<string, HTMLElement>());
  const newsStreamElement = useRef<HTMLDivElement>(null);
  const previousNewsPositions = useRef(new Map<string, number>());
  const handledDeliverySignature = useRef('');
  const hasMeasuredNewsLayout = useRef(false);
  const measuredSessionId = useRef<number>();

  useEffect(() => {
    if (!id || news_fetched) return;
    dispatch(fetchPassedNews({ session_id: id }));
  }, [dispatch, id, news_fetched]);

  const groupedNews = useMemo(
    () => groupV4News(news || [], groupingMode),
    [groupingMode, news],
  );
  const groupLabels = useMemo(
    () => Object.keys(groupedNews).sort((a, b) => a.localeCompare(b)),
    [groupedNews],
  );

  useEffect(() => {
    if (
      groupingMode === 'all' ||
      (activeGroup !== ALL_NEWS && !groupLabels.includes(activeGroup))
    ) {
      setActiveGroup(ALL_NEWS);
    }
  }, [activeGroup, groupLabels, groupingMode]);

  const groupedVisibleNews =
    activeGroup === ALL_NEWS ? news || [] : groupedNews[activeGroup] || [];
  const latestBatchIds = useMemo(
    () => new Set(Object.keys(lastNews || {})),
    [lastNews],
  );
  const newIds = useMemo(
    () =>
      currentPeriodNewsIds === undefined
        ? latestBatchIds
        : new Set(currentPeriodNewsIds.map(String)),
    [currentPeriodNewsIds, latestBatchIds],
  );
  const scopeVisibleNews =
    scope === 'new'
      ? groupedVisibleNews.filter(item => newIds.has(String(item.id)))
      : groupedVisibleNews;
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visibleNews = normalizedSearch
    ? scopeVisibleNews.filter(item =>
        [
          displayNewsHeadline(item),
          item.headline,
          item.geography,
          ...(item.sectors || []),
        ]
          .filter(Boolean)
          .some(value =>
            String(value).toLocaleLowerCase().includes(normalizedSearch),
          ),
      )
    : scopeVisibleNews;

  useLayoutEffect(() => {
    const nextPositions = new Map<string, number>();
    visibleNews.forEach(item => {
      const newsId = String(item.id);
      const element = newsItemElements.current.get(newsId);
      if (element) nextPositions.set(newsId, element.offsetTop);
    });

    const latestDeliveryIds = Object.keys(lastNews || {});
    const deliverySignature = `${id}:${latestDeliveryIds.join(',')}`;
    const isUnseenDelivery =
      currentPeriodNewsIds !== undefined &&
      lastNewsSource === 'socket' &&
      latestDeliveryIds.length > 0 &&
      handledDeliverySignature.current !== deliverySignature;

    if (!hasMeasuredNewsLayout.current || measuredSessionId.current !== id) {
      hasMeasuredNewsLayout.current = true;
      measuredSessionId.current = id;
      previousNewsPositions.current = nextPositions;
      if (isUnseenDelivery) {
        handledDeliverySignature.current = deliverySignature;
      }
      return;
    }

    if (isUnseenDelivery) {
      handledDeliverySignature.current = deliverySignature;
      const prefersReducedMotion =
        window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ??
        false;
      const isViewingTopOfFeed =
        (newsStreamElement.current?.scrollTop ?? 0) <= 8;
      if (
        !prefersReducedMotion &&
        isViewingTopOfFeed &&
        !draggedStory.current
      ) {
        const latestDeliveryIdSet = new Set(latestDeliveryIds);
        visibleNews.forEach(item => {
          const newsId = String(item.id);
          const element = newsItemElements.current.get(newsId);
          if (!element || typeof element.animate !== 'function') return;

          const previousTop = previousNewsPositions.current.get(newsId);
          const currentTop = nextPositions.get(newsId);
          if (previousTop !== undefined && currentTop !== undefined) {
            const offset = previousTop - currentTop;
            if (Math.abs(offset) > 0.5) {
              element.animate(
                [
                  { transform: `translateY(${offset}px)` },
                  { transform: 'translateY(0)' },
                ],
                {
                  duration: NEWS_DELIVERY_ANIMATION_MS,
                  easing: NEWS_DELIVERY_EASING,
                },
              );
            }
          } else if (latestDeliveryIdSet.has(newsId)) {
            element.animate(
              [
                { opacity: 0, transform: 'translateY(-10px)' },
                { opacity: 1, transform: 'translateY(0)' },
              ],
              {
                duration: NEWS_DELIVERY_ANIMATION_MS,
                easing: NEWS_DELIVERY_EASING,
              },
            );
          }
        });
      }
    }

    previousNewsPositions.current = nextPositions;
  }, [currentPeriodNewsIds, id, lastNews, lastNewsSource, visibleNews]);

  return (
    <section
      className="markets-v4-news"
      aria-labelledby="markets-v4-news-title"
    >
      <header className="markets-v4-news__head">
        <div>
          <h2 id="markets-v4-news-title">News</h2>
          <span>
            {visibleNews.length}{' '}
            {visibleNews.length === 1 ? 'story' : 'stories'}
          </span>
        </div>
        <label className="markets-v4-news__search">
          <SearchOutlined aria-hidden="true" />
          <input
            type="search"
            value={search}
            aria-label="Search news"
            placeholder="Search news"
            onChange={event => setSearch(event.target.value)}
          />
        </label>
        <div
          className="markets-v4-news__scope"
          role="group"
          aria-label="News view"
        >
          {(['all', 'new'] as NewsScope[]).map(value => (
            <button
              key={value}
              type="button"
              className={scope === value ? 'is-active' : ''}
              aria-pressed={scope === value}
              onClick={() => setScope(value)}
            >
              {value === 'new' ? 'New' : 'All'}
            </button>
          ))}
        </div>
      </header>

      {groupingMode !== 'all' && (
        <nav className="markets-v4-news__filters" aria-label="Filter news">
          {[ALL_NEWS, ...groupLabels].map(label => {
            const selected = label === activeGroup;
            const buttonLabel = label === ALL_NEWS ? 'All' : label;
            return (
              <button
                key={label}
                type="button"
                className={selected ? 'is-active' : ''}
                aria-pressed={selected}
                onClick={() => setActiveGroup(label)}
              >
                {buttonLabel}
              </button>
            );
          })}
        </nav>
      )}

      <div
        ref={newsStreamElement}
        className="markets-v4-news__stream"
        aria-busy={fetching_news || lastNews === undefined}
        aria-live="polite"
      >
        {(fetching_news || lastNews === undefined) && !visibleNews.length ? (
          <div className="markets-v4-news__state">
            <LoadingOutlined spin />
            <strong>Loading news</strong>
          </div>
        ) : visibleNews.length ? (
          visibleNews.map(item => {
            const isNew = newIds.has(String(item.id));
            const headline = displayNewsHeadline(item);
            const geography = normalizedGeography(item.geography);
            const sectors = (item.sectors || [])
              .map(normalizedSector)
              .filter(Boolean);
            return (
              <article
                key={item.id}
                ref={element => {
                  const newsId = String(item.id);
                  if (element) newsItemElements.current.set(newsId, element);
                  else newsItemElements.current.delete(newsId);
                }}
                className={`markets-v4-news-item${isNew ? ' is-latest' : ''}${
                  dragEnabled ? ' is-draggable' : ''
                }${draggedStory.current?.dataset.newsId === String(item.id) && draggedStory.current.classList.contains('is-dragging') ? ' is-dragging' : ''}`}
                data-news-id={item.id}
                draggable={dragEnabled}
                title={
                  dragEnabled
                    ? 'Drag this story onto an eligible instrument'
                    : undefined
                }
                onDragStartCapture={event => {
                  recordDragDiagnostic('source.start', {
                    news: Number(item.id), drag_enabled: dragEnabled,
                  }, true);
                  if (!dragEnabled) {
                    event.preventDefault();
                    return;
                  }
                  const newsId = Number(item.id);
                  if (!Number.isInteger(newsId) || newsId <= 0) {
                    event.preventDefault();
                    return;
                  }
                  const written = writeV4NewsDragData(event.dataTransfer, {
                    newsId,
                    timing: isNew ? 'current' : 'past',
                  });
                  recordDragDiagnostic('source.payload', { news: newsId, written }, true);
                  if (!written) {
                    event.preventDefault();
                    clearDrag();
                    return;
                  }
                  // Preserve the existing dimming without re-rendering the
                  // news feed or measuring its layout just to start a drag.
                  markDraggedAfterStart(event.currentTarget);
                }}
                onDragEnd={clearDrag}
              >
                <div className="markets-v4-news-item__content">
                  {isNew && (
                    <div className="markets-v4-news-item__meta">
                      <span className="is-new">New</span>
                    </div>
                  )}
                  <h3>{headline}</h3>
                  {(geography !== 'Unknown' || sectors.length > 0) && (
                    <div className="markets-v4-news-item__tags">
                      {geography !== 'Unknown' && <span>{geography}</span>}
                      {sectors.slice(0, 2).map(sector => (
                        <span key={sector}>{sector}</span>
                      ))}
                      {sectors.length > 2 && <span>+{sectors.length - 2}</span>}
                    </div>
                  )}
                </div>
              </article>
            );
          })
        ) : (
          <div className="markets-v4-news__state">
            <strong>
              {normalizedSearch
                ? 'No matching stories'
                : scope === 'new'
                  ? 'No new stories'
                  : 'No news yet'}
            </strong>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsFeed;
